import React from 'react';
import { useSelector } from 'react-redux';
import NotFound from '@/components/error-pages/NotFound';
import { Route, RouteProps } from 'react-router-dom';
import { checkCondition } from '@/components/permissions/helpers';
import { PermissionCheck, ProfilePermissions, ProjectPermissions } from '@/types';
import { IState } from '@/state/IState';

export interface AuthRouteConditions {
    profile?: PermissionCheck<ProfilePermissions>;
    project?: PermissionCheck<ProjectPermissions>;
}

type AuthRouteProps<T extends {}> = Omit<RouteProps, 'component'> & {
    component: React.ComponentType<T>;
    fallbackComponent?: React.ComponentType<any>;
    conditions?: AuthRouteConditions;
    extraProps?: Partial<T>;
};

function AuthRoute<T extends {}>({
    component: Component,
    fallbackComponent: FallbackComponent,
    conditions,
    extraProps,
    ...rest
}: AuthRouteProps<T>) {
    const profile = useSelector((state: IState) => state.global.activeProfile);
    const project = useSelector((state: IState) => state.global.activeProject);

    function isPermitted() {
        let permitted = false;

        if (!conditions) {
            return true;
        }

        const { profile: profileCondition, project: projectCondition } = conditions;

        if (profile && profileCondition) {
            if (checkCondition(profile.permissions, profileCondition)) {
                permitted = true;
            }
        }

        if (project && projectCondition) {
            if (checkCondition(project.permissions, projectCondition)) {
                permitted = true;
            }
        }

        return permitted;
    }

    return (
        <Route
            {...rest}
            render={(props) => {
                const mergedProps = {
                    profileId: profile ? profile.id : null,
                    ...props,
                    ...props.match.params,
                    ...extraProps,
                };

                if (isPermitted()) {
                    const Comp = Component as any; //Workaround
                    return <Comp {...mergedProps} />;
                } else if (FallbackComponent) {
                    return <FallbackComponent {...mergedProps} />;
                }

                return <NotFound />;
            }}
        />
    );
}

export default AuthRoute;